import type { Action, ThunkAction } from '@reduxjs/toolkit'
import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, useStore } from "react-redux";
import userReducer from "./userSlice";
import organizationReducer from "./organizationSlice";
import roleReducer from "./roleSlice";
import permissionsReducer from "./permissionsSlice";
import featuresReducer from "./featuresSlice";
import publicBookingFlowReducer from "../app/booking-flow/publicBookingFlowSlice";
import bookingCalendarReducer from "../app/booking-calendar/bookingCalendarSlice";
import { api } from "../services/api";
import { publicApi } from "../services/publicApi";
import { devToolsOptions } from "./devTools";
import staffSessionReducer from "./staffSessionSlice";
import benefitReadReducer from "@/features/benefit-workspace/benefitReadSlice";
import { createStaffSessionListener } from "./staffSessionListener";
import benefitEditors from "@/features/benefit-workspace/benefitEditorSlice";
import { benefitApi } from "@/features/benefit-workspace/benefitApi";
import { createBenefitEditorListener } from "@/features/benefit-workspace/benefitEditorListener";

import benefitCreation from "@/features/benefit-workspace/benefitCreationSlice";
import { createBenefitCreationListener } from "@/features/benefit-workspace/benefitCreationListener";
import { api as legacyApi } from "@/lib/api";

const benefitCreationListener = createBenefitCreationListener(attempt => legacyApi.post(
  attempt.kind === "person" ? "/api/people" : "/api/benefitapplications", attempt.body), benefitApi);
const staffSessionListener = createStaffSessionListener(api);
const benefitEditorListener = createBenefitEditorListener(benefitApi);

export const store = configureStore({
  reducer: {
    staffSession: staffSessionReducer,
    benefitReads: benefitReadReducer,
    benefitEditors,
    benefitCreation,
    user: userReducer,
    organization: organizationReducer,
    role: roleReducer,
    permissions: permissionsReducer,
    features: featuresReducer,
    publicBookingFlow: publicBookingFlowReducer,
    bookingCalendar: bookingCalendarReducer,
    [api.reducerPath]: api.reducer,
    [publicApi.reducerPath]: publicApi.reducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().prepend(staffSessionListener.middleware, benefitEditorListener.middleware, benefitCreationListener.middleware).concat(api.middleware, publicApi.middleware),
  // Off in production; metadata-only debugging view elsewhere. See devTools.ts.
  devTools: devToolsOptions(process.env.NODE_ENV),
});

export type AppStore = typeof store
export type RootState = ReturnType<AppStore['getState']>
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore['dispatch']
// Define a reusable type describing thunk functions
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>

// Pre-typed hooks — use these instead of plain useDispatch/useSelector
export const useAppStore = useStore.withTypes<AppStore>();
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
